import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "pas-de-son-ordinateur",
  silo: "materiel",
  h1: "Plus de son sur mon ordinateur Windows : causes et solutions simples",
  metaTitle: "Pas de son sur PC Windows : retrouver le son rapidement",
  metaDescription: "Le son a disparu de votre PC ? Pilotes audio, paramètres Windows, câble HDMI... Voici comment retrouver le son en quelques étapes.",
  excerpt: "Le son a mystérieusement disparu de votre ordinateur ? Avant de paniquer, sachez que ce problème vient dans 80% des cas d'un simple réglage ou d'un pilote à reinstaller. Voici comment le résoudre.",
  publishedAt: "2024-02-10",
  readingMinutes: 5,
  relatedSlugs: ["webcam-micro-ne-fonctionne-pas", "imprimante-non-detectee", "logiciel-plante-ne-souvre-pas", "mise-a-jour-windows-bloquee"],
  faq: [
    {
      question: "Le son fonctionne sur YouTube mais pas dans mes jeux ou autres applications -- pourquoi ?",
      answer: "Chaque application peut avoir son propre niveau de volume dans Windows. Faites un clic droit sur l'icône son > Ouvrir le mixeur de volume. Vérifiez que le curseur de l'application concernée n'est pas à zéro.",
    },
    {
      question: "Mon PC a un son très faible même au maximum -- comment l'améliorer ?",
      answer: "Dans les propriétés du périphérique de lecture (clic droit icône son > Sons > onglet Lecture > Propriétés), allez dans l'onglet Améliorations et activez 'Égaliseur' ou 'Amplification du volume'. Certains pilotes Realtek proposent aussi une option pour amplifier le volume au-delà de 100%.",
    },
    {
      question: "Après avoir branché un casque, le son de mes haut-parleurs ne revient plus -- comment faire ?",
      answer: "Clic droit sur l'icône son > Sons > onglet Lecture. Cliquez droit sur vos haut-parleurs et sélectionnez 'Définir comme périphérique par défaut'.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Pas de son sur un PC Windows est un problème très courant, surtout après une mise à jour Windows ou le branchement d'un nouveau périphérique. Commencez par les vérifications les plus simples avant d'aller vers les solutions plus techniques.",
    },
    {
      type: "h2",
      text: "Vérifications de base",
    },
    {
      type: "ul",
      items: [
        "Le volume Windows n'est pas à zéro ou coupé (icône son en bas à droite de l'écran)",
        "Le câble audio des haut-parleurs est correctement branché",
        "Si vous utilisez un casque : il est bien connecté au bon port (vert = sortie audio)",
        "Le mixeur de volume (clic droit icône son) -- chaque application a son propre curseur",
      ],
    },
    {
      type: "h2",
      text: "Problème avec le périphérique de sortie audio sélectionné",
    },
    {
      type: "p",
      text: "Après avoir branché un écran HDMI ou un nouveau casque, Windows peut changer automatiquement le périphérique de sortie audio par défaut. Cliquez sur l'icône son en bas à droite et vérifiez quel périphérique est sélectionné. Changez-le pour retrouver vos haut-parleurs ou le bon périphérique.",
    },
    {
      type: "h2",
      text: "Mettre à jour le pilote audio",
    },
    {
      type: "p",
      text: "C'est la cause la plus fréquente après une mise à jour Windows. Dans le Gestionnaire de périphériques (clic droit Démarrer), développez 'Contrôleurs audio, vidéo et jeux'. Clic droit sur votre carte son > Mettre à jour le pilote. Si ça ne change rien, désinstallez le pilote et redémarrez -- Windows le réinstallera automatiquement.",
    },
    {
      type: "callout",
      variant: "tip",
      title: "Pilote Realtek",
      text: "La plupart des cartes mères utilisent des puces audio Realtek. Si vous perdez le son après une mise à jour Windows, téléchargez directement le pilote Realtek HD Audio depuis le site de votre fabricant de carte mère ou depuis realtek.com.",
    },
    {
      type: "h2",
      text: "Redémarrer le service audio Windows",
    },
    {
      type: "p",
      text: "Appuyez sur Windows + R, tapez 'services.msc'. Cherchez 'Windows Audio' dans la liste. Clic droit > Redémarrer. Faites de même pour 'Windows Audio Endpoint Builder'. Cela relance le moteur audio de Windows sans redémarrer le PC.",
    },
    {
      type: "h2",
      text: "Utiliser le dépanneur audio Windows",
    },
    {
      type: "p",
      text: "Clic droit sur l'icône son > Dépanner les problèmes de son. Windows détecte et corrige automatiquement les problèmes les plus courants. C'est souvent la solution la plus rapide.",
    },
    {
      type: "h2",
      text: "Pas de son après une connexion HDMI",
    },
    {
      type: "p",
      text: "Le câble HDMI transporte à la fois l'image et le son. Si vous branchez votre PC sur un téléviseur ou un moniteur via HDMI, Windows peut router le son vers le téléviseur plutôt que vers vos haut-parleurs. Sélectionnez le bon périphérique de sortie dans les paramètres son. Ce type de problème est similaire aux soucis de [webcam et microphone](slug:webcam-micro-ne-fonctionne-pas) -- toujours vérifier le périphérique sélectionné par défaut.",
    },
  ],
};

export default article;
